import { useState, useMemo } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { cn } from '@/lib/utils';
import { LinkedHabit } from '@/hooks/useActionLinkedHabits';
import { LinkedHabitsPopover } from './LinkedHabitsPopover';
import { Check, ChevronRight, TrendingUp } from 'lucide-react';
import { format } from 'date-fns';

interface MilestoneActionItemProps {
  action: string;
  completed: boolean;
  matchingHabit?: { title: string; streak: number } | null;
  linkedHabits?: LinkedHabit[];
  habitProgress?: number; // 0-100 from last 30 days
  onToggle: () => void;
  onAddHabit?: () => void;
}

// Helper to get dates for last N days
const getDateRange = (days: number): string[] => {
  const dates: string[] = [];
  const today = new Date();
  for (let i = 0; i < days; i++) {
    const date = new Date(today);
    date.setDate(date.getDate() - i);
    dates.push(format(date, 'yyyy-MM-dd'));
  }
  return dates;
};

export const MilestoneActionItem = ({
  action,
  completed,
  matchingHabit,
  linkedHabits = [],
  habitProgress: externalHabitProgress,
  onToggle,
  onAddHabit,
}: MilestoneActionItemProps) => {
  const [showPopover, setShowPopover] = useState(false);

  const today = format(new Date(), 'yyyy-MM-dd');
  const completedTodayCount = linkedHabits.filter(h => 
    h.completedDates.includes(today)
  ).length;
  const hasLinkedHabits = linkedHabits.length > 0;

  // Calculate habit progress locally if not provided
  const habitProgress = useMemo(() => {
    if (externalHabitProgress !== undefined) return externalHabitProgress;
    if (linkedHabits.length === 0) return 0;

    const last30Days = getDateRange(30);
    let totalCompletions = 0;

    linkedHabits.forEach(habit => {
      const completionsInRange = habit.completedDates.filter(date => 
        last30Days.includes(date)
      ).length;
      totalCompletions += completionsInRange;
    });

    const maxPossible = 30 * linkedHabits.length;
    return Math.min(100, Math.round((totalCompletions / maxPossible) * 100));
  }, [linkedHabits, externalHabitProgress]);

  return (
    <li className="flex flex-col gap-1.5 text-sm group">
      <div className="flex items-start gap-3">
        <Checkbox
          checked={completed}
          onCheckedChange={onToggle}
          className="mt-0.5 shrink-0"
        />
        <div className="flex-1">
          <span className={cn(completed && 'text-muted-foreground line-through')}>
            {action}
          </span>
          {completed && (
            <Badge variant="outline" className="ml-2 text-xs bg-primary/10 text-primary border-primary/20">
              ✓ Complete
            </Badge>
          )}
          {matchingHabit && !hasLinkedHabits && !completed && (
            <Badge variant="outline" className="ml-2 text-xs bg-accent/10">
              🔥 {matchingHabit.streak} day streak
            </Badge>
          )}
        </div>
      </div>

      {/* Linked habits indicator with progress */}
      {hasLinkedHabits && !completed && (
        <div className="ml-7 space-y-1.5">
          {/* Progress bar for habit-based progress */}
          {habitProgress > 0 && (
            <div className="flex items-center gap-2">
              <Progress value={habitProgress} className="h-1.5 flex-1 max-w-[200px]" />
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <TrendingUp className="w-3 h-3" />
                {habitProgress}% from habits
              </span>
            </div>
          )}
          
          {/* Habit completion status */}
          <button
            onClick={() => setShowPopover(true)}
            className={cn(
              "flex items-center gap-2 text-[13px] transition-colors group/link",
              completedTodayCount > 0
                ? "text-green-500 hover:text-green-400"
                : "text-muted-foreground hover:text-foreground"
            )}
          >
            <Check className="w-3.5 h-3.5" />
            <span>
              Daily habits: {completedTodayCount}/{linkedHabits.length} completed today
            </span>
            <span className="text-primary underline-offset-2 group-hover/link:underline flex items-center gap-0.5">
              View linked habits
              <ChevronRight className="w-3.5 h-3.5" />
            </span>
          </button>
        </div>
      )}

      {/* Show link when completed but has habits */}
      {hasLinkedHabits && completed && (
        <button
          onClick={() => setShowPopover(true)}
          className="ml-7 text-[13px] text-muted-foreground hover:text-foreground transition-colors flex items-center gap-1"
        >
          {linkedHabits.length} linked habit{linkedHabits.length > 1 ? 's' : ''}
          <ChevronRight className="w-3.5 h-3.5" />
        </button>
      )}

      {/* Popover for habit details */}
      <LinkedHabitsPopover
        isOpen={showPopover}
        onClose={() => setShowPopover(false)}
        actionText={action}
        habits={linkedHabits}
        onAddHabit={() => {
          setShowPopover(false);
          onAddHabit?.();
        }}
      />
    </li>
  );
};
